import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { 
  Users, FileText, MessageSquare, Phone, Settings, LogOut, Menu, X, Home, User, Briefcase
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const adminMenuItems = [
    { path: '/admin', icon: Home, label: 'Dashboard', roles: ['admin', 'user'] },
    { path: '/admin/team', icon: Users, label: 'Mitarbeiter', roles: ['admin'] },
    { path: '/admin/jobs', icon: Briefcase, label: 'Stellenanzeigen', roles: ['admin', 'user'] },
    { path: '/admin/treatments', icon: FileText, label: 'Behandlungsfelder', roles: ['admin'] },
    { path: '/admin/popups', icon: MessageSquare, label: 'Popup-Fenster', roles: ['admin', 'user'] },
    { path: '/admin/contact', icon: Phone, label: 'Kontaktdaten', roles: ['admin', 'user'] },
    { path: '/admin/users', icon: User, label: 'Benutzer', roles: ['admin'] },
    { path: '/admin/settings', icon: Settings, label: 'Einstellungen', roles: ['admin'] },
  ];

  const filteredMenu = adminMenuItems.filter(item => item.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f7f5' }}>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-md px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <span className="font-semibold" style={{ color: '#1B4D3E' }}>Admin Panel</span>
        <div className="w-6" />
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 h-full w-64 bg-white shadow-lg transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="p-6 border-b">
          <h1 className="text-xl font-bold" style={{ color: '#1B4D3E' }}>
            Admin Panel
          </h1>
          <p className="text-sm text-gray-500 mt-1">Zahnarztpraxis</p>
        </div>

        <nav className="p-4 space-y-2">
          {filteredMenu.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}
                `}
                style={isActive ? { backgroundColor: '#1B4D3E' } : {}}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold" style={{ backgroundColor: '#1B4D3E' }}>
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-sm">{user?.username}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 w-full px-4 py-2 rounded-lg hover:bg-red-50"
          >
            <LogOut className="w-5 h-5" />
            <span>Abmelden</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
